import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import { MdCloudUpload } from "react-icons/md";
import { STATUS_CODES } from "constants/index";
import { ORDERS_FROM_FILE_URL } from "constants/urls";
import { buildOrderErrorMessage } from "helpers/orders";
import {
  getArticleLocationSuggestionErrorMessage,
  isArticleLocationSuggestionError,
} from "helpers/articleLocationSuggestions";
import useCrud from "hooks/useCrud";
import useFileUpload from "hooks/useFileUpload";
import type { OrderFromFileCreationResult } from "types";

type CreationState = {
  status: "idle" | "creating" | "success" | "error";
  result: OrderFromFileCreationResult | null;
  error: string | null;
};

type UploadAttempt = { creating: boolean };

type Props = { onCompleted?: (result: OrderFromFileCreationResult) => void };

const INITIAL_CREATION: CreationState = { status: "idle", result: null, error: null };

export default function OrderFileUpload({ onCompleted }: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [creation, setCreation] = useState<CreationState>(INITIAL_CREATION);
  const attemptRef = useRef<UploadAttempt | null>(null);
  const { upload, reset, status, progress, error, uploadedFile } = useFileUpload();
  const { createData } = useCrud<{ pdfFileId: number }>(ORDERS_FROM_FILE_URL, {
    disableAutoFetch: true,
  });
  const isUploading = status === "requesting" || status === "uploading" || status === "confirming";
  const isCreating = creation.status === "creating";
  const isBusy = isUploading || isCreating;
  const fileError = selectedFile && selectedFile.type !== "application/pdf"
    ? "Seleccione un archivo PDF para cargar el remito."
    : error;

  useEffect(() => () => {
    attemptRef.current = null;
  }, []);

  async function createOrder(pdfFileId: number, attempt: UploadAttempt) {
    attempt.creating = true;
    setCreation({ status: "creating", result: null, error: null });
    try {
      if (!Number.isSafeInteger(pdfFileId) || pdfFileId <= 0) {
        throw new Error("El identificador del archivo no es valido.");
      }
      const response = await createData({ pdfFileId }, true);
      if (attemptRef.current !== attempt) return;
      if (response?.status !== STATUS_CODES.OK) {
        const details: string[] = Array.isArray(response?.data?.errors)
          ? response.data.errors.map((detail: unknown) =>
            typeof detail === "string" || isArticleLocationSuggestionError(detail)
              ? getArticleLocationSuggestionErrorMessage(detail)
              : buildOrderErrorMessage(detail, ""),
          ).filter((message: unknown): message is string => typeof message === "string" && Boolean(message.trim()))
          : [];
        throw new Error(details.length > 0
          ? Array.from(new Set(details)).join("\n")
          : buildOrderErrorMessage({ message: response?.data?.message }, "No se pudo crear el pedido."));
      }
      const results: unknown = response.data;
      if (!Array.isArray(results) || results.length !== 1 || !results[0] ||
        !Number.isSafeInteger(results[0].order) || results[0].order <= 0 ||
        typeof results[0].numero_remito !== "string" || !results[0].numero_remito.trim()) {
        throw new Error("La API devolvio una respuesta de creacion de pedido invalida.");
      }
      setCreation({ status: "success", result: results[0], error: null });
    } catch (error) {
      if (attemptRef.current === attempt) {
        setCreation({
          status: "error",
          result: null,
          error: error instanceof Error ? error.message : "No se pudo crear el pedido.",
        });
      }
    } finally {
      if (attemptRef.current === attempt) attemptRef.current = null;
    }
  }

  async function handleSelect(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file || attemptRef.current) return;

    reset();
    setCreation(INITIAL_CREATION);
    setSelectedFile(file);
    if (file.type !== "application/pdf") return;

    const attempt: UploadAttempt = { creating: false };
    attemptRef.current = attempt;
    const confirmed = await upload(file);
    if (attemptRef.current !== attempt) return;
    if (!confirmed) {
      attemptRef.current = null;
      return;
    }
    await createOrder(confirmed.fileId, attempt);
  }

  function handleRetryCreation() {
    if (!uploadedFile || creation.status !== "error" || attemptRef.current) return;
    const attempt: UploadAttempt = { creating: false };
    attemptRef.current = attempt;
    void createOrder(uploadedFile.fileId, attempt);
  }

  function handleClose() {
    // Closing cannot undo a creation already processed by the API.
    if (attemptRef.current?.creating) return;
    const result = creation.result;
    attemptRef.current = null;
    reset();
    setCreation(INITIAL_CREATION);
    setSelectedFile(null);
    if (result) onCompleted?.(result);
  }

  return (
    <>
      <Button component="label" variant="contained" startIcon={<MdCloudUpload />} disabled={isBusy}>
        Cargar Remito
        <input
          type="file"
          accept="application/pdf"
          aria-label="Seleccionar remito PDF"
          hidden
          disabled={isBusy}
          onChange={handleSelect}
        />
      </Button>
      <Dialog open={selectedFile !== null} onClose={handleClose} disableEscapeKeyDown={isCreating} fullWidth maxWidth="sm" aria-labelledby="order-file-upload-title">
        <DialogTitle id="order-file-upload-title">Cargar remito PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, overflowWrap: "anywhere" }}>
            {selectedFile?.name}
          </Typography>
          {isBusy && (
            <Box role="status" aria-live="polite" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {isCreating ? "Creando pedido..." : status === "requesting" ? "Solicitando permiso de carga..." :
                  status === "confirming" ? "Verificando archivo..." : `Subiendo archivo: ${progress}%`}
              </Typography>
              <LinearProgress
                aria-label="Progreso de carga"
                variant={!isCreating && status === "uploading" ? "determinate" : "indeterminate"}
                value={progress}
              />
            </Box>
          )}
          {fileError && <Alert severity="error">{fileError}</Alert>}
          {creation.error && (
            <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
              {creation.error} El archivo ya esta confirmado; puede reintentar la creacion sin subirlo nuevamente.
            </Alert>
          )}
          {creation.result && (
            <Alert severity="success">
              Pedido #{creation.result.order} guardado correctamente. Remito {creation.result.numero_remito}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isCreating}>Cerrar</Button>
          {creation.status === "error" && uploadedFile && (
            <Button variant="contained" onClick={handleRetryCreation}>Reintentar creacion</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
